<template>
    <div class="SupportPage">
        <SiteHeader/>

        <Container>
            <div class="header">
                <h1>Help &amp; Support</h1>
                <p>
                    Below you'll find answers to the most common questions you may have on DumpTek. If you still can't find the answer you're looking for, we're just an email or call away.
                </p>
            </div>
        </Container>

        <Container class="learn">
            <LearnSection/>
        </Container>

        <Container class="faq">
            <Row>
                <Column :md="8">
                    <h2>Frequently asked questions</h2>
                    <Accordion
                        v-for="(faq, index) in faqs"
                        :key="index"
                        :title="faq.question"
                        :description="faq.answer"
                    />
                </Column>
                <Column :md="4">
                    <div class="aside">
                        <h2>United States</h2>
                        <div class="section">
                            <strong>Email</strong>
                            <a v-if="user" href="mailto:info@dumptek.com">info@dumptek.com</a>
                            <router-link v-else to="/contact">Send Message</router-link>
                        </div>
                        <div class="section">
                            <strong>Phone</strong>
                            <a href="tel:8007735612">(800) 773-5612</a>
                        </div>
                    </div>
                </Column>
            </Row>
        </Container>

        <SiteFooter/>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import Accordion from '@/components/Accordion';
import LearnSection from '@/components/LearnSection';

export default {
    name: 'SupportPage',

    components: {
    SiteHeader,
    SiteFooter,
    Accordion,
    LearnSection,
},

    data: () => ({
        faqs: [
            {
                question: 'Can I cancel at any time?',
                answer: 'Yes, you can cancel your DumpTek subscription at any time.',
            },
            {
                question: 'What do I need to get set up?',
                answer: 'Nothing, DumpTek is a cloud based software only solution with no need to install any networks or onsite servers. You can start using DumpTek in a matter of minutes.',
            },
            {
                question: 'Will the app work offline in a dead zone?',
                answer: 'Yes, drivers can create and work on tickets offline and can submit them. As soon as they have internet access the ticket will be submitted automatically.',
            },
            {
                question: 'Do I need to pay extra for the mobile app?',
                answer: 'No, the app is free to download but cost per driver user account. Please see our pricing for more information.',
            },
            {
                question: 'How does Dumptek round times in the totals section of its haul sheets?',
                answer: 'DumpTek offers a few options for rounding times on haul sheets and reports. Rounding preferences can be found in company settings. You can change your selection at any time you wish. Please <a href="https://docs.google.com/document/d/119Mf1euwKCvgjpY0F7qq6n_dujsZSCo4jrEPC6CUwqk" target="_blank">click here</a> to learn more about the calculations used.',
            },
            {
                question: 'Can I edit a ticket after it has been submitted?',
                answer: 'Yes, Managers have the ability to edit driver tickets.',
            },
            {
                question: 'Can multiple users access my DumpTek company account at the same time?',
                answer: 'Yes, you can add multiple users to access and manage your company data.',
            },
            {
                question: 'Can I access DumpTek on my mobile phone or tablet?',
                answer: 'DumpTek can be accessed from any web enabled mobile device with a web browser. The experience is optimized for drivers on our DumpTek mobile apps available on iOS and Android devices.',
            },
            {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major credit and debit cards - Visa, MasterCard and AMEX.',
            },
            {
                question: 'Can I still access my data from the admin portal if I no longer have a subscription?',
                answer: 'Yes, admin and manager accounts do not require active driver subscriptions and can still access all data for their company.',
            },
            {
                question: 'Will my account ever be deleted if I cancel or allow my subscription to expire?',
                answer: 'No, your account will not be deleted if your subscription is canceled or expired. You may, however, request to have your have your account deleted by contacting customer support.',
            },
            {
                question: 'Can I download a copy of my data?',
                answer: 'All of the reports generated in DumpTek can be exported in Excel or PDF.',
            },
            {
                question: 'Why am I getting errors when trying to run reports?',
                answer: 'This is likely due to a data mismatch either from the tickets themselves or the criteria you are trying to use to run the report. For example; running a project report for a project that is billed by the material, and a driver uses a material on their ticket not included in the project set up will cause an error. Please reach out to customer service for assistance if the problem persists.'
            },
        ],
    }),

    computed: {
        ...mapState('auth', ['user']),
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.SupportPage {
    .header {
        max-width: 700px;
        margin: 120px auto 80px;
        text-align: center;
        position: relative;
        z-index: 1;

        h1 {
            @extend %display-subtitle;
            margin-bottom: 15px;
        }

        p {
            @extend %display-description;
        }
    }

    .faq {
        margin-top: 120px;
        margin-bottom: 60px;

        h2 {
            margin-bottom: $grid-gutter-width;
        }
    }

    .aside {
        padding: 30px;
        border-radius: $border-radius;
        background-color: $bg-color-3;

        strong {
            display: block;
        }

        .section + .section {
            margin-top: $grid-gutter-width;
        }
    }
}
</style>
